@if (vm.confirmedBookingLoaded) {
  <div id="page-root" class="max-w-824">
    <section class="header">
      <section class="icon-wrapper">
        <i class="fa-light fa-circle-check"></i>
        <h1 class="heading-1">
          {{ title }}
        </h1>
      </section>
      <p class="body mt-8 email-info">
        We’ve emailed you these details. You can also
        <button
          aria-label="download a copy"
          class="body bold email-info download"
          (click)="handleDownload()"
          data-testid="download-booking-confirmation"
        >
          download a copy</button
        >.
      </p>
    </section>

    @if (vm.isSPBooking && !vm.isTVReplacement && !vm.isFloorCareProduct) {
      <section data-testid="sp_contact" class="sp__booking__section" data-testid="sp-banner">
        <p class="body">
          {{ vm.serviceProvider }} will contact you directly to book a date and time.
          @if (vm.isVokera) {
            They'll aim to call within 45 minutes during opening hours – or on the next working day if it's out of
            hours.
          }
        </p>
      </section>
    }

    <section class="booking mt-32 sm-mt-24 mb-64 sm-mb-40">
      <section class="container">
        <app-maintenance-banner [brand]="vm.manufacturer" />

        @if (vm.manualReferralOption) {
          <app-manual-signposting
            [repairer]="vm.bookingOEM"
            [contactNumber]="vm.serviceProviderTelephone"
            [postCode]="vm.postCode"
          />
        }

        @if (vm.isCollection) {
          <section class="block-wrapper collection-banner" data-testid="collection-banner">
            <p class="body large">{{ collectionBannerMessage }}</p>
          </section>
        }

        @if (vm.isSelfSend) {
          <section class="block-wrapper collection-banner" data-testid="self-send-banner">
            <p class="body large">We’ll post you the packaging and pre-paid envelope.</p>
          </section>
        }

        @if (vm.isPayAndClaim) {
          <section class="block-wrapper collection-banner" data-testid="pay-and-claim-banner">
            <p class="body large">Your claim has been accepted. You can now contact a local repairer directly.</p>
          </section>
        }

        <ng-container>
          <section class="container" [class.pay-and-claim-format]="vm.isPayAndClaim">
            <section class="column col-1">
              @if (vm.showPaymentBox) {
                <div class="block-wrapper booking-box">
                  <h2 class="heading-5">Payment summary</h2>

                  <p class="body bold mt-12" data-testid="payment-summary-type">
                    {{ vm.paymentDetails.type | lowercase | firstLetterCapitalise }}
                  </p>
                  <p class="body mt-4" data-testid="payment-summary-line">
                    £{{ vm.paymentDetails.amount | customRound }} {{ vm.paymentDetails.paidLabel }} paid
                  </p>

                  @if (vm.paymentDetails.billingAddress?.length) {
                    <p class="body bold mt-12">Billing address</p>
                    @for (line of vm.paymentDetails.billingAddress || []; track line) {
                      <p class="body" qmBlock>
                        {{ line }}
                      </p>
                    }
                  }
                </div>
              }
              @if (vm.isPayAndClaim) {
                <ng-container *ngTemplateOutlet="repairAndQuoteBox"></ng-container>
              }
              @if (!vm.isPayAndClaim) {
                <ng-container *ngTemplateOutlet="bookingBox"></ng-container>
              }
              @if (!vm.isCollection && !vm.isSelfSend && !vm.isPayAndClaim) {
                <ng-container *ngTemplateOutlet="repairerBox"></ng-container>
              }
            </section>

            <section class="column col-2">
              @if (
                (!vm.isSPBooking || vm.isFloorCareProduct) &&
                !vm.isManualReferral &&
                !vm.manualReferralOption &&
                !vm.isPayAndClaim &&
                !vm.isTVReplacement
              ) {
                <ng-container *ngTemplateOutlet="mmrBox"></ng-container>
              }

              @if (!vm.isPayAndClaim) {
                <ng-container *ngTemplateOutlet="nextStepsBox"></ng-container>
              } @else {
                <ng-container *ngTemplateOutlet="invoiceBox"></ng-container>
              }
              @if (!vm.isPayAndClaim) {
                <ng-container *ngTemplateOutlet="contactBox"></ng-container>
              }
            </section>
          </section>
        </ng-container>
        @if (vm.isPayAndClaim) {
          <section class="pay-and-claim-contact">
            <ng-container *ngTemplateOutlet="contactBox"></ng-container>
          </section>
        }
      </section>
    </section>

    <ng-template #repairAndQuoteBox>
      <div class="block-wrapper repair-and-quote-box" data-testid="repair-and-quote-box">
        <h2 class="heading-5">Find a repairer and get a quote</h2>

        <p class="label mt-12 non-gas">
          <i class="fa-light fa-plug-circle-bolt"></i>
          For non-gas appliances and TVs
        </p>
        <p class="body mt-4">
          <a class="link-text" href="https://www.electricalcompetentperson.co.uk/" target="_blank"
            >Hire a registered electrician</a
          >
          via the Registered Competent Person website.
        </p>
        <div class="divider mt-12 mb-12"></div>

        <p class="label gas">
          <i class="fa-light fa-triangle-exclamation"></i>
          For gas appliances
        </p>

        <p class="body mt-4 gas-safety">
          <a class="link-text" href="https://www.gassaferegister.co.uk/" target="_blank">Find a Gas Safe engineer</a> on
          the Gas Safe Register. <br />Or call on <a href="tel:08004085500">0800 408 5500</a>.
        </p>
        <div class="divider mt-12 mb-12"></div>

        <p class="body">
          If their quote is over £300 for appliances or over £250 for TVs, please email for approval before booking the
          repair.
        </p>
        <p class="body mt-8">We’ll need your full name, plan number and the total cost, including VAT.</p>

        <div class="divider mt-12 mb-12"></div>

        <p class="body bold">Plan number</p>
        <p class="body mt-4" data-testid="plan-number">{{ vm.planNumber }}</p>
      </div>
    </ng-template>

    <ng-template #invoiceBox>
      <div class="block-wrapper invoice-box" data-testid="invoice-box">
        <h2 class="heading-5">Send us the invoice</h2>

        <p class="body mt-12">After the appointment, ask the repairer for an invoice. It should include:</p>

        <ul class="mt-8">
          <li>
            <p class="body">Their business details</p>
          </li>
          <li>
            <p class="body">A VAT registration number</p>
          </li>
          <li>
            <p class="body">The full breakdown of costs (for example, parts, labour, call-out charges, VAT)</p>
          </li>

          <li>
            <p class="body">Your name, address and plan number</p>
          </li>
        </ul>

        <p class="label mt-12">Email it to us:</p>

        <section class="email-link">
          <i class="fa-light fa-envelope"></i>
          <a
            href="mailto:repair_reimbursement@domesticandgeneral.com"
            class="link-text"
            data-testid="reimbursement-email"
          >
            repair_reimbursement@domesticandgeneral.com
          </a>
        </section>

        <!--        <p class="label mt-12">Or post it to:</p>-->

        <!--        <p class="body">-->
        <!--          FAO Claims, <br />-->
        <!--          Leicester House, <br />-->
        <!--          17 Leicester Street, <br />-->
        <!--          Bedworth, <br />-->
        <!--          Warwickshire, <br />-->
        <!--          CV12 8JP-->
        <!--        </p>-->

        <p class="body mt-12">
          We’ll refund the amount to the bank account linked to your Direct Debit – or send you a cheque if that’s not
          possible. Either way, we'll issue the refund within 30 days of receiving your invoice.
        </p>
      </div>
    </ng-template>

    <ng-template #bookingBox>
      <div class="block-wrapper booking-box">
        <h2 class="heading-5">
          {{ vm.bookingBoxTitle }}
        </h2>
        @for (items of vm.bookingSummaryItems; track items.label) {
          <p class="body bold mt-12">{{ items.label }}</p>

          @if (items.label === 'Plan number' && vm.isGameReplacement) {
            <p class="body mt-4 plan-will-expire" data-testid="plan-will-expire">
              <i class="fa-light fa-circle-exclamation"></i>
              <span> Your plan will end in line with the terms and conditions </span>
            </p>
          } @else if (items.encrypt) {
            <p class="body mt-4" [attr.data-testid]="items.testId" qmEncrypt>
              {{ items.value }}
            </p>
          } @else {
            <p class="body mt-4" [attr.data-testid]="items.testId">
              {{ items.value }}
            </p>
          }
        }
        @if (vm.vcFeatureFlag) {
          <div class="divider mt-16 mb-16"></div>

          <app-vulnerable-customers> </app-vulnerable-customers>
        }
      </div>
    </ng-template>

    <ng-template #repairerBox>
      <div class="block-wrapper repairer-box" data-testid="repairer-box">
        <h2 class="heading-5">
          {{ vm.repairerBoxTitle }}
        </h2>

        <p class="body bold mt-12">
          {{ vm.repairerLabel }}
        </p>
        <p class="body mt-4" data-testid="repairer-name">{{ vm.serviceProvider }}</p>

        @if (vm.bookingOEM !== 'whirlpool' && vm.bookingOEM !== 'hoover candy' && vm.serviceProviderEmail) {
          <p class="body bold mt-12">Email</p>
          <section class="email-link" data-testid="repairer-email">
            <i class="fa-light fa-envelope"></i>
            <a [href]="'mailto:' + vm.serviceProviderEmail" class="link-text" data-testid="repairer-email">
              {{ vm.serviceProviderEmail }}
            </a>
          </section>
        }

        <p class="body bold mt-12">Phone</p>
        <app-telephone-link [phoneNumber]="vm.serviceProviderTelephone" data-testid="repairer-phone" class="mt-4" />
        <p class="call-cost hint mt-8" data-testid="call-cost-messaging">
          Calls cost the basic rate per minute plus your phone company’s access charge. From a mobile, they can cost
          much more. Calls to 0800 numbers are always free.
        </p>

        <div class="divider mt-16 mb-16 md-mt-16 md-mb-16"></div>

        <app-contact-hours [contactHours]="vm.serviceProviderContactHours" />
      </div>
    </ng-template>

    <ng-template #mmrBox>
      <div class="block-wrapper mmr-box" data-testid="mmr-box">
        <h2 class="heading-5">Stay up to date</h2>
        <p class="body mt-8">
          {{ mmrMessage }}
        </p>

        <app-button
          dataLabel="mmr-button"
          class="md-ml-0 sm-full-width mt-16"
          [buttonOptions]="{
            variant: 'secondary',
            isLink: true,
            url: vm.mmrLink,
            target: '_blank',
          }"
          (onClick)="trackClick(); clearConfirmedBooking()"
          data-testid="manage-my-repair-btn"
        >
          Manage my {{ vm.claimTypeWord }}
          <i class="fa-light fa-arrow-right ml-8"></i>
        </app-button>
      </div>
    </ng-template>

    <ng-template #nextStepsBox>
      <div class="block-wrapper next-steps-box" data-testid="next-steps-box">
        <h2 class="heading-5 mb-12">What happens next?</h2>

        @for (nextStep of vm.nextSteps; track nextStep.title; let index = $index) {
          @if (index !== 0) {
            <div class="divider mt-12 mb-12"></div>
          }
          <p class="body bold" data-testid="next-step-title">
            <i
              [class]="'fa-light fa-lg mr-8 ' + nextStep.icon"
              [ngStyle]="{
                color: nextStep.color,
              }"
            ></i>
            {{ nextStep.title }}
          </p>
          <p class="body mt-4" [innerHTML]="nextStep.description" data-testid="next-step-description"></p>
        }
      </div>
    </ng-template>

    <ng-template #contactBox>
      <div class="block-wrapper contact-box">
        <h2 class="heading-5">Your contact details</h2>

        <p class="body bold mt-12">Address</p>
        <section class="flex-col mt-4 address">
          @for (line of vm.address; track line) {
            <p qmBlock class="body address-line" data-testid="customer-address">
              {{ line }}
            </p>
          }
        </section>

        @if (vm.phoneNumber) {
          <p class="body bold mt-12">Phone</p>
          <p class="body mt-4" qmBlock data-testid="customer-phone">{{ vm.phoneNumber }}</p>
        }

        @if (vm.email) {
          <p class="body bold mt-12">Email</p>
          <p class="body mt-4" qmBlock data-testid="customer-email">{{ vm.email }}</p>
        }
      </div>
    </ng-template>
  </div>
}

<section style="position: absolute; top: 0; left: 0; z-index: -1" id="copy-content"></section>
